import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { SubmitButton } from "@/react/components/SettingsApp/Plan/RegularPlan/SubmitButton";
import {
  capitalizeFirstLetter,
  getNextTier,
} from "@/react/helpers/planHelpers";
import { getPreviousTier, planTiers } from "@/react/helpers/planHelpers";
import { isCurrent } from "@/react/helpers/plansHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import type { Plan } from "@circle-react/types";
import { Typography } from "@circle-react-uikit/Typography";
import { AnnualDiscount } from "../shared/AnnualDiscount";
import { FeaturesList } from "../shared/FeaturesList";

const localeRoot = "pricing_plans.plan_settings.plan_selection.plan_selector";

interface PlanCardProps {
  plan: Plan;
}

export const PlanCard = ({ plan }: PlanCardProps) => {
  const { isCommunityOnTrial, currentPlanTier, isCommunityOnPaidPlan } =
    usePlanStatus();
  const isCurrentPlan = isCurrent(plan.plan_action);
  const shouldHighlight =
    (isCommunityOnTrial && plan.tier == planTiers.professional) ||
    (isCommunityOnPaidPlan && getNextTier(currentPlanTier) == plan.tier);

  const isHighlightOnTop = isCurrentPlan || shouldHighlight;

  const featuresListTitle = useMemo(() => {
    if (plan.tier === planTiers.basic) {
      return t(`${localeRoot}.plan_card.core_features`);
    }

    if (plan.tier === planTiers.professional) {
      return t(`${localeRoot}.plan_card.professional_features`);
    }

    return t(`${localeRoot}.plan_card.everything_plus`, {
      tier: capitalizeFirstLetter(getPreviousTier(plan.tier) ?? ""),
    });
  }, [plan]);

  return (
    <div className="flex flex-col">
      {isCurrentPlan && (
        <div className="bg-tertiary border-primary rounded-t-2xl py-1 text-center">
          <Typography.LabelXs>
            {t(`${localeRoot}.plan_card.current_plan`)}
          </Typography.LabelXs>
        </div>
      )}
      {shouldHighlight && (
        <div className="border-primary rounded-t-2xl bg-indigo-600 py-1 text-center">
          <Typography.LabelXs color="text-white">
            {t(`${localeRoot}.plan_card.recommended`)}
          </Typography.LabelXs>
        </div>
      )}

      <div
        className={classNames(
          "border-primary flex h-[600px] w-[300px] flex-col items-start gap-y-4 rounded-2xl border px-6 py-5 lg:w-[310px]",
          isHighlightOnTop && "rounded-t-none",
          !isHighlightOnTop && "mt-8",
        )}
      >
        <div>
          <Typography.TitleSm
            color="text-tertiary"
            weight="font-semibold"
            size="text-base"
          >
            {plan.display_name}
          </Typography.TitleSm>
          <Typography.TitleLg weight="font-semibold">
            {plan.display_base_monthly_amount}
          </Typography.TitleLg>
          <div>
            <AnnualDiscount plan={plan} />
          </div>
        </div>
        <div>
          <Typography.BodySm weight="font-normal">
            {t(`${localeRoot}.tier_descriptions.${plan.tier}`)}
          </Typography.BodySm>
        </div>

        <SubmitButton plan={plan} />
        <div className="flex flex-col gap-y-3">
          <div>
            <Typography.LabelXs weight="semibold">
              {featuresListTitle}
            </Typography.LabelXs>
          </div>
          <div className="flex flex-col gap-y-1">
            <FeaturesList
              plan={plan}
              shouldHighlightFeatures={shouldHighlight}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
